// Comprehensive list of amenities with Lucide icons.
// Each entry has multiple `match` regexes so admin-typed strings in TR/EN are auto-mapped.
import {
    Wifi, Coffee, Car, Wind, Utensils, BedDouble,
    Waves, Dumbbell, Sparkles, ShowerHead, Tv,
    Refrigerator, ClipboardCheck, Briefcase, Building2,
    Baby, Cigarette, CigaretteOff, Bell, Snowflake,
    Bath, ConciergeBell, ParkingCircle, Plane,
    Accessibility, Languages, Soup,
    ChefHat, Volume2, Bus, ArrowUpDown,
    Heart, Shirt, ScrollText, Compass, Banknote,
    PawPrint, Cookie, FlameKindling,
} from "lucide-react";

export const AMENITY_DEFS = [
    { key: "wifi", icon: Wifi, label: "Ücretsiz WiFi", patterns: [/wifi|kablosuz|wireless|internet/i] },
    { key: "breakfast", icon: Coffee, label: "Kahvaltı Dahil", patterns: [/kahvalt|breakfast/i] },
    { key: "ac", icon: Wind, label: "Klima", patterns: [/^klima$|air[\s-]?cond/i] },
    { key: "heating", icon: Snowflake, label: "Isıtma", patterns: [/heating|kalorifer|ısıtma/i] },
    { key: "parking", icon: ParkingCircle, label: "Otopark", patterns: [/parking|otopark|valet|vale/i] },
    { key: "restaurant", icon: Utensils, label: "Restoran", patterns: [/restaurant|resto|restoran/i] },
    { key: "buffet", icon: Soup, label: "Açık Büfe", patterns: [/buffet|büfe/i] },
    { key: "chef", icon: ChefHat, label: "Şef Hizmeti", patterns: [/şef|chef/i] },
    { key: "pool", icon: Waves, label: "Yüzme Havuzu", patterns: [/pool|havuz/i] },
    { key: "gym", icon: Dumbbell, label: "Fitness/Spor Salonu", patterns: [/gym|fitness|spor salonu/i] },
    { key: "spa", icon: Sparkles, label: "SPA & Wellness", patterns: [/spa|wellness/i] },
    { key: "hammam", icon: Bath, label: "Hamam", patterns: [/hammam|hamam/i] },
    { key: "shower", icon: ShowerHead, label: "Duş", patterns: [/duş|shower/i] },
    { key: "tv", icon: Tv, label: "TV", patterns: [/^tv$|television|televizyon/i] },
    { key: "fridge", icon: Refrigerator, label: "Mini Buzdolabı", patterns: [/fridge|buzdolab|mini bar|minibar/i] },
    { key: "concierge", icon: ConciergeBell, label: "7/24 Resepsiyon", patterns: [/resepsiyon|reception|concierge|7\/?24/i] },
    { key: "haram_shuttle", icon: Bus, label: "Haram/Mescid Servisi", patterns: [/haram|servis|shuttle|umre transferi/i] },
    { key: "airport", icon: Plane, label: "Havalimanı Transferi", patterns: [/havalimanı|airport|cidde transferi/i] },
    { key: "elevator", icon: ArrowUpDown, label: "Asansör", patterns: [/asansör|elevator|lift/i] },
    { key: "laundry", icon: Shirt, label: "Çamaşır Hizmeti", patterns: [/çamaşır|laundry|kuru temizleme/i] },
    { key: "iron", icon: ClipboardCheck, label: "Ütü", patterns: [/iron|ütü/i] },
    { key: "family", icon: Baby, label: "Aile Dostu", patterns: [/aile|family|çocuk dostu/i] },
    { key: "kids", icon: Cookie, label: "Çocuk Menüsü", patterns: [/çocuk menüsü|kids menu/i] },
    { key: "accessible", icon: Accessibility, label: "Engelli Erişimi", patterns: [/engelli|accessible|tekerlekli/i] },
    { key: "languages", icon: Languages, label: "Türkçe Konuşan Personel", patterns: [/türkçe|turkish[-\s]?speaking|multilingual/i] },
    { key: "smoking", icon: Cigarette, label: "Sigara İçilen Alan", patterns: [/sigara içilebilir|smoking allowed/i] },
    { key: "non_smoking", icon: CigaretteOff, label: "Sigarasız Oda", patterns: [/sigarasız|non[-\s]?smoking|smoke[-\s]?free/i] },
    { key: "prayer_room", icon: Compass, label: "Mescit", patterns: [/mescit|prayer room|namaz odası/i] },
    { key: "quran", icon: ScrollText, label: "Kuran-ı Kerim", patterns: [/kuran|quran/i] },
    { key: "business", icon: Briefcase, label: "İş Merkezi", patterns: [/business|iş merkezi|toplantı/i] },
    { key: "currency", icon: Banknote, label: "Döviz Bürosu", patterns: [/döviz|currency exchange/i] },
    { key: "safe", icon: Bell, label: "Kasa", patterns: [/kasa|safe(?!ty)/i] },
    { key: "luggage", icon: Briefcase, label: "Bagaj Depolama", patterns: [/luggage|bagaj/i] },
    { key: "bbq", icon: FlameKindling, label: "Mangal/BBQ", patterns: [/bbq|mangal|barbecue/i] },
    { key: "pets", icon: PawPrint, label: "Evcil Hayvan Kabulü", patterns: [/pet|evcil/i] },
    { key: "honeymoon", icon: Heart, label: "Balayı Süiti", patterns: [/balayı|honeymoon/i] },
    { key: "soundproof", icon: Volume2, label: "Ses Yalıtımı", patterns: [/sound[-\s]?proof|ses yalıtım/i] },
    { key: "rooftop", icon: Building2, label: "Teras", patterns: [/rooftop|teras/i] },
    { key: "ensuite", icon: BedDouble, label: "Ensuite Banyo", patterns: [/ensuite|banyo/i] },
];

/** Match raw amenity strings to icon defs. Returns deduped list of {label, icon}. */
export const resolveAmenities = (amenities = []) => {
    const matched = new Map();
    for (const raw of amenities) {
        const txt = String(raw || "").trim();
        if (!txt) continue;
        const def = AMENITY_DEFS.find((d) => d.patterns.some((p) => p.test(txt)));
        if (def && !matched.has(def.key)) {
            matched.set(def.key, { label: def.label, icon: def.icon, originalText: txt });
        } else if (!def) {
            // Unknown amenity — keep with a fallback Sparkles icon
            const fallbackKey = `_custom_${txt.toLowerCase()}`;
            if (!matched.has(fallbackKey)) {
                matched.set(fallbackKey, { label: txt, icon: Sparkles, originalText: txt });
            }
        }
    }
    return Array.from(matched.values());
};
